import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
  type PageTab,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useAccountHasEntitlement from "@common/hooks/useAccountHasEntitlement";
import { useV2GetClientQuery } from "@generated";
import { ClientForm } from "../ClientForm/ClientForm";
import { ContactsTab } from "../Contacts/ContactsTab";
import { TermsConditions } from "../TermsAndConditions/TermsConditions";
import { useClientMutations, useClientFormData } from "../api";
import { emptyClientForm } from "../types";
import type { ClientEditTab, ClientFormValues } from "../types";

const DETAILS_FORM_ID = "client-form";
const TERMS_FORM_ID = "terms-conditions-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { hasEntitlement } = useAccountHasEntitlement();
  const { update } = useClientMutations();
  const {
    rateTables,
    projectVariables,
    isLoading: formDataLoading,
  } = useClientFormData();
  const {
    data,
    isLoading: clientLoading,
    refetch,
  } = useV2GetClientQuery(
    {
      id: id,
      include: ["rate-table"],
    },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ClientFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<ClientEditTab>("client");

  const tabs: PageTab[] = [
    { key: "client", label: "Client" },
    { key: "contacts", label: "Contacts" },
    ...(hasEntitlement("terms-and-conditions")
      ? [{ key: "special_conditions", label: "Terms & Conditions" }]
      : []),
  ];

  const loadedInitial = useMemo<ClientFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyClientForm;
    const udfValues: Record<string, string> = {};
    (attrs["user-defined-fields"] ?? []).forEach(
      (u: { name: string; value?: string }) => {
        udfValues[u.name] = u.value ?? "";
      }
    );
    const rateTableId = (
      data?.data?.relationships?.["rate-table"]?.data as
        | { id?: number }
        | undefined
    )?.id;
    return {
      name: attrs.name ?? "",
      domain: attrs.domain ?? "",
      msaDate: attrs["msa-date"] ?? "",
      rateTableId: rateTableId ? String(rateTableId) : "",
      udfValues,
    };
  }, [data]);

  if (clientLoading || formDataLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: ClientFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values, projectVariables);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update client", err);
      setToast(saveErrorToast(err, "client"));
    } finally {
      setSubmitting(false);
    }
  };

  const formId =
    activeTab === "client"
      ? DETAILS_FORM_ID
      : activeTab === "special_conditions"
      ? TERMS_FORM_ID
      : undefined;

  return (
    <>
      <AdminFormPage
        title="Edit Client"
        formId={formId}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as ClientEditTab)}
      >
        {activeTab === "client" && (
          <ClientForm
            key={formKey}
            id={DETAILS_FORM_ID}
            initialValues={initialValues}
            rateTables={rateTables}
            projectVariables={projectVariables}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {activeTab === "contacts" && <ContactsTab clientId={id} />}
        {activeTab === "special_conditions" && (
          <TermsConditions
            clientId={id}
            formId={TERMS_FORM_ID}
            onSubmittingChange={setSubmitting}
          />
        )}
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
